import { Link } from "react-router-dom";
import { Scissors, Sparkles, ShieldCheck, Zap, CircleDollarSign, ArrowRight, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Logo } from "@/components/Logo";
import { ToolIcon } from "@/components/iconMap";
import { CROP_TOOL_LIST } from "@/data/tools";
import { GENERIC_TOOLS } from "@/data/moreTools";
import { AI_TOOL_LIST } from "@/data/aiTools";

const FEATURED = CROP_TOOL_LIST.filter((t) => t.mode === "marketplace");
const ALL = [...CROP_TOOL_LIST, ...GENERIC_TOOLS];

const BADGES = [
  { icon: CircleDollarSign, label: "100% Free Forever" },
  { icon: Zap, label: "No Registration Required" },
  { icon: ShieldCheck, label: "PDF Tools Run In Your Browser" },
];

function Card({ t, accent }) {
  const iconBg = accent === "indigo" ? "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-600" : "bg-blue-50 text-blue-600 group-hover:bg-blue-600";
  return (
    <Link to={t.path} data-testid={`home-tool-${t.slug}`}
      className="group relative bg-white border border-slate-200 hover:border-blue-400 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/5 hover:-translate-y-1 flex flex-col">
      <div className={`w-12 h-12 rounded-xl ${iconBg} group-hover:text-white transition-colors flex items-center justify-center mb-4`}>
        <ToolIcon name={t.icon} className="w-6 h-6" />
      </div>
      <h3 className="font-heading font-bold text-lg text-slate-900">{t.name}</h3>
      <p className="mt-2 text-sm text-slate-600 flex-1">{t.promise}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600">
        Open <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </span>
    </Link>
  );
}

export default function Home() {
  return (
    <>
      <Seo title="Ecroply AI — Free E-commerce Label Cropper & AI Seller Toolkit"
        description="Crop Amazon, Flipkart, Meesho & Shopsy shipping labels in seconds. Free browser-based label croppers, PDF tools and AI listing generators. No login."
        path="/" />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 grid-dots opacity-10" />
        <div className="absolute -top-32 -right-20 w-[34rem] h-[34rem] bg-blue-600/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-[30rem] h-[30rem] bg-indigo-600/20 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-20 sm:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-float-up">
              <span className="inline-flex items-center gap-2 bg-white/10 text-blue-200 border border-white/15 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" /> Smart Tools for E-commerce Sellers
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">
                Free E-commerce Label Cropper & <span className="brand-text bg-clip-text">AI Seller Toolkit</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
                Crop Amazon, Flipkart, Meesho & Shopsy shipping labels in seconds. No login, 100% free, browser-based and privacy-focused.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/tools/meesho-shipping-label-crop" data-testid="hero-cta-crop"
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl font-semibold shadow-lg shadow-blue-500/25 transition-colors">
                  <Scissors className="w-5 h-5" /> Crop Shipping Labels
                </Link>
                <Link to="/ai-tools" data-testid="hero-cta-ai"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 px-6 py-3.5 rounded-xl font-semibold transition-colors">
                  <Sparkles className="w-5 h-5" /> Explore AI Listing Tools
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                {BADGES.map((b) => (
                  <span key={b.label} className="inline-flex items-center gap-2 text-sm text-slate-300">
                    <b.icon className="w-4 h-4 text-blue-400" /> {b.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative animate-float-up" style={{ animationDelay: "0.15s" }}>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 shadow-2xl">
                <div className="flex items-center gap-2 mb-4"><Logo textClass="text-white" /></div>
                <div className="grid grid-cols-2 gap-3">
                  {FEATURED.map((t) => (
                    <Link key={t.slug} to={t.path} className="bg-white/90 hover:bg-white rounded-xl p-4 transition-colors group">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <ToolIcon name={t.icon} className="w-5 h-5" />
                      </div>
                      <p className="text-sm font-semibold text-slate-800">{t.badge ? t.badge[0].toUpperCase() + t.badge.slice(1) : t.name}</p>
                      <p className="text-xs text-slate-500">Label crop</p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured croppers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="max-w-2xl mb-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">Shipping label croppers</h2>
          <p className="mt-3 text-base text-slate-600">Clean, print-ready labels with the invoice pages removed — one marketplace per tool, tuned to its real layout.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURED.map((t) => <Card key={t.slug} t={t} />)}
        </div>
      </section>

      {/* All tools */}
      <section id="all-tools" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">All free tools</h2>
            <p className="mt-3 text-base text-slate-600">Everything you need to prep dispatch and polish listings — no account, no paywall.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ALL.map((t) => <Card key={t.slug} t={t} />)}
          </div>
        </div>
      </section>

      {/* AI teaser */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="rounded-3xl brand-gradient p-8 sm:p-12 text-white relative overflow-hidden">
          <div className="absolute inset-0 grid-dots opacity-10" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-medium mb-5"><Sparkles className="w-4 h-4" /> AI Listing Tools</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">Write titles, descriptions, bullets & keywords in seconds</h2>
            <p className="mt-4 text-blue-50 max-w-xl">Describe your product and let AI draft marketplace-ready copy you can tweak and publish.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              {AI_TOOL_LIST.slice(0, 5).map((t) => (
                <Link key={t.slug} to={t.path} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-4 py-2 text-sm font-medium transition-colors" data-testid={`home-ai-${t.slug}`}>
                  <ToolIcon name={t.icon} className="w-4 h-4" /> {t.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Privacy callout */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-slate-900 text-slate-200 rounded-2xl p-6 sm:p-8 border border-slate-800 flex items-start gap-4">
          <ShieldCheck className="w-8 h-8 text-blue-400 shrink-0" />
          <div>
            <h3 className="font-heading font-bold text-white text-lg">Privacy by design</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              Our PDF and image tools run entirely inside your browser — your shipping labels and invoices are never uploaded to a server. The AI listing tools are the exception: the text you type there is sent to an AI service to generate your result, which we state clearly on those pages.
            </p>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2">
              {["No file uploads for PDF & image tools", "No account or login", "No pricing, no paid tiers", "Works on phone and desktop"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-emerald-400" /> {x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
